import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  AfterViewInit,
  Component,
  ContentChildren,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  QueryList,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

/**
 * Simple Scroll Area with an uninvasive scrollbar
 */
@Component({
  selector: 'ag-scroll-area',
  templateUrl: './scroll-area.component.html',
  styleUrls: ['./scroll-area.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ScrollAreaComponent implements AfterViewInit, OnDestroy {
  resizeObserver: ResizeObserver;
  @Input()
  get hiddenScroll() {
    return this._hiddenScroll;
  }
  set hiddenScroll(value: BooleanInput) {
    this._hiddenScroll = coerceBooleanProperty(value);
  }
  @Input() size: number;
  @Input() height: string;
  @Input() width: string;
  @Input() alignContent: 'left' | 'center' | 'right' = 'left';
  @Input()
  get horizontalScroll() {
    return this._horizontalScroll;
  }
  set horizontalScroll(value: BooleanInput) {
    this._horizontalScroll = coerceBooleanProperty(value);
  }
  @Input()
  get verticalScroll() {
    return this._verticalScroll;
  }
  set verticalScroll(value: BooleanInput) {
    this._verticalScroll = coerceBooleanProperty(value);
  }
  @ViewChild('scrollAreaContainer') scrollAreaContainer: ElementRef<HTMLElement>;
  @ContentChildren('projectedContent') projectedContentList: QueryList<ElementRef>;

  private _hiddenScroll = false;
  private _verticalScroll = true;
  private _horizontalScroll = true;

  constructor(private _elementref: ElementRef<HTMLElement>) {}

  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent): void {
    if (this.horizontalScroll) {
      event.preventDefault();
      this.scrollAreaContainer.nativeElement.scrollLeft += event.deltaY;
    }
  }

  ngAfterViewInit(): void {
    // Set Scroll Type
    if (this._verticalScroll) {
      this.scrollAreaContainer.nativeElement.classList.add('vertical-scroll');
    }
    if (this._horizontalScroll) {
      this.scrollAreaContainer.nativeElement.classList.add('horizontal-scroll');
    }
    if (this._hiddenScroll) {
      this.scrollAreaContainer.nativeElement.classList.add('hidden-scroll');
    }

    this._updateContentWidth();
    this._observeContentChanges();
  }

  _updateContentWidth(): void {
    this._setContainerSize(
      this._elementref.nativeElement.clientHeight,
      this._elementref.nativeElement.clientWidth,
    );
  }

  _observeContentChanges(): void {
    // Observe host resize
    this.resizeObserver = new ResizeObserver((entries) => {
      this._setContainerSize(
        this._elementref.nativeElement.clientHeight,
        this._elementref.nativeElement.clientWidth,
      );
    });

    this.resizeObserver.observe(this._elementref.nativeElement);

    // Observe projected content change
    this.projectedContentList.changes.subscribe(() => {
      this._updateContentWidth();
    });
  }

  _setContainerSize(height: number, width: number) {
    this.scrollAreaContainer.nativeElement.style.width = width + 'px';
    this.scrollAreaContainer.nativeElement.style.height = height ? height + 'px' : null;
  }

  ngOnDestroy() {
    this.resizeObserver.disconnect();
  }
}
